import moment from 'moment-timezone';
import Countdown from '@services/Countdown';

export const getDateTimeFormatObject = (global, incomingDatePartOrder) => {
  const defaultDatePartOrder = global.date_time_formats.default_date_part_order;
  const dateTimeFormatMap = global.date_time_formats.ordered_formats || {};

  const datePartOrder = incomingDatePartOrder || defaultDatePartOrder;

  return dateTimeFormatMap[datePartOrder];
};

export const dateMoment = (inp, format, language, strict) => moment(inp, format, language, strict);
export const currentTime = () => dateMoment();
export const utc = (inp, strict) => moment.utc(inp, strict);
export const today = () => moment().startOf('day');
export const isToday = momentDate => momentDate.isSame(today(), 'd');

export const oneMonthInFuture10AM = () => {
  const now = moment();
  const oneMonthAhead = now.add(1, 'M'); // M = month

  return oneMonthAhead.set('hour', 10).set('minute', 0).set('second', 0).set('millisecond', 0);
};

export const prettyTime = time => {
  let format = 'LT'; // 10:03 AM

  if (!isToday(moment(time))) format = 'D MMM'; // 10 Sep

  return moment(time).format(format);
};

export const prettyDateTime = (
  time,
  datePartOrder,
  global,
  dateFormatKey = 'longSlashDate',
  timeFormatKey = 'hoursMinutesMeridiemWithTimezone',
  dateOnly = false
) => {
  const dateTimeFormat = getDateTimeFormatObject(global, datePartOrder);
  const timezone = moment.tz.guess(true) || '';
  const timeInZone = moment.tz(time, timezone);
  const renderedDate = timeInZone.format(dateTimeFormat[dateFormatKey]);
  const renderedTime = timeInZone.format(dateTimeFormat[timeFormatKey]);

  let renderedString = renderedDate;

  if (!dateOnly) renderedString += `, ${renderedTime}`;

  return renderedString;
};

export const isBefore = (date, comparisonDate) => moment(date).isBefore(comparisonDate);
export const isAfter = (date, comparisonDate) => moment(date).isAfter(comparisonDate);
export const isBetween = (date, start, end) => moment(date).isBetween(start, end);

export const SAT_SUN_DAYS = [6, 0]; // Saturday, Sunday

// Tombstone - Delete this commented code after Jan 1, 2022.
// export const ALL_WEEKEND_DAYS = [5, 6, 0, 1]; // Friday, Saturday, Sunday, Monday
// export const WEEKEND_START_DAY = 5; // Friday
// export const WEEKEND_END_DAY = 1; // Monday
// export const WEEKEND_START_HOUR = 18; // 6 pm
// export const WEEKEND_END_HOUR = 8; // 8 am

export const isDuringWeekend = incomingDate => {
  if (!incomingDate) return false;

  const date = moment(incomingDate);
  const weekday = date.day();
  // const isWeekendDay = ALL_WEEKEND_DAYS.includes(weekday);

  // Friday - Monday?
  // if (!isWeekendDay) return false;

  const isSatOrSun = SAT_SUN_DAYS.includes(weekday);

  // Saturday or Sunday?
  if (isSatOrSun) return true;

  // Tombstone - Delete this commented code after Jan 1, 2022.
  // If we want to check a specific duration between a start and end time and not just Saturday
  // or Sunday, uncomment this code and adjust.
  // const hourOfDay = date.hour();
  // const minuteOfHour = date.minute();
  // const isAfterWeekendStartHour = hourOfDay > WEEKEND_START_HOUR;
  // const isWeekendStartHour = hourOfDay === WEEKEND_START_HOUR;
  // const isBeforeWeekendEndHour = hourOfDay < WEEKEND_END_HOUR;

  // // After Friday @ 6pm?
  // if (isAfterWeekendStartHour || (isWeekendStartHour && minuteOfHour > 0)) return true;

  // // Before Monday @ 8am?
  // if (isBeforeWeekendEndHour) return true;

  return false;
};

// Convert a number into years
export const inYears = (num, unit = 'years') => {
  let lengthInYears = num;

  if (unit === 'weeks') lengthInYears = num / 52;
  else if (unit === 'months') lengthInYears = num / 12;

  return lengthInYears;
};

export const inMinutes = (num, unit) => {
  let timeInMinutes = num;

  if (unit === 'hours') timeInMinutes = num * 60;
  else if (unit === 'days') timeInMinutes = num * 24 * 60;

  return timeInMinutes;
};

export const timesFromMinutes = timeInMinutes => ({
  days: (timeInMinutes / 60) / 24,
  hours: timeInMinutes / 60,
  minutes: timeInMinutes
});

export const humanizeTimezone = (zoneString = '') => zoneString.replace('_', ' ').replace('/', ' / ');

// Find the unit that most closely matches the time in minutes
// 600 minutes = 10 hours
export const unitFromMinutes = timeInMinutes => {
  const timeObj = timesFromMinutes(timeInMinutes);

  if (timeObj.days >= 1 && (timeObj.days % 1 === 0)) return 'days';
  if (timeObj.hours >= 1 && (timeObj.hours % 1 === 0)) return 'hours';

  return 'minutes';
};

export const prettyTimeZone = (time = moment()) => {
  const timezone = moment.tz.guess(true) || '';
  const zoneObject = moment.tz.zone(timezone) || {};

  return `${humanizeTimezone(timezone)} (${zoneObject.abbr(time)})`;
};

export const dateCountdown = (start, end) => (
  Countdown.startClock(end, start)
);

export const utcCountdown = time => {
  if (!time) return null;
  const now = utc();
  const endTime = utc(time);

  if (!isAfter(endTime, now)) return null;

  return Countdown.startClock(endTime, now);
};

const duration = (inp, unit) => moment.duration(inp, unit);

export const utcDuration = (time, unit='milliseconds') => {
  if (!time) return null;
  const now = utc();
  const endTime = utc(time);

  if (!isAfter(endTime, now)) return null;

  return duration(endTime.diff(now, unit), unit);
};

export const formatDateTime = (date, dateFormat, timeFormat, timezone) => {
  if (!date) {
    return '-';
  }
  const momentDate = moment.tz(date, timezone);

  if (!momentDate || !momentDate.isValid()) {
    return '-';
  }
  const formattedDay = momentDate.format('ddd');
  const formattedDate = momentDate.format(dateFormat);
  const formattedTime = momentDate.format(timeFormat).toUpperCase();
  const formattedTimezone = momentDate.format('z');

  return { formattedDate, formattedDay, formattedTime, formattedTimezone };
};
